import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shell, PageHeader } from '../components/Shell.jsx';

export default function Command() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch('/api/dashboard', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setErr('failed to load dashboard'));
  }, []);

  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const loading = !data && !err;
  const p = data?.prospects?.by_status || {};
  const pi = data?.pitches?.by_status || {};

  const drafts = pi.drafted || 0;
  const sent = pi.sent || 0;
  const replied = data?.pitches?.replied_total || 0;
  const totalActiveProspects = data?.prospects?.active || 0;

  return (
    <Shell>
      <PageHeader
        eyebrow={today}
        title="Command"
        sub="Your morning view. Approve drafts, add prospects, watch the pipeline."
        right={<Link to="/queue" className="btn">Open approval queue</Link>}
      />

      {err ? <div className="card card-pad" style={{ borderColor: 'var(--danger)', marginBottom: 'var(--space-6)' }}><span style={{ color: 'var(--danger)' }}>{err}</span></div> : null}

      <div className="grid-4 stack-8" style={{ marginTop: 'var(--space-8)' }}>
        <Stat label="Active prospects" value={loading ? '—' : totalActiveProspects} meta="Excludes archived" />
        <Stat label="Drafts pending" value={loading ? '—' : drafts} meta="Awaiting your approval" emphasis={drafts > 0} />
        <Stat label="Sent" value={loading ? '—' : sent} meta="All time" />
        <Stat label="Replies" value={loading ? '—' : replied} meta="From real humans" />
      </div>

      <div className="grid-2" style={{ marginTop: 'var(--space-8)' }}>
        <Pipeline counts={p} loading={loading} />
        <NextSteps prospectStats={p} drafts={drafts} loading={loading} />
      </div>
    </Shell>
  );
}

function Stat({ label, value, meta, emphasis = false }) {
  return (
    <div className="stat" style={emphasis ? { borderColor: 'var(--accent)' } : null}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={emphasis && value > 0 ? { color: 'var(--accent)' } : null}>{value}</div>
      <div className="stat-meta">{meta}</div>
    </div>
  );
}

const STAGES = [
  { key: 'discovered',   label: 'Discovered',   color: '#a5a298' },
  { key: 'enriched',     label: 'Enriched',     color: '#7a786f' },
  { key: 'scored',       label: 'Scored',       color: '#4a4a44' },
  { key: 'drafted',      label: 'Drafted',      color: '#1e2c55' },
  { key: 'approved',     label: 'Approved',     color: '#3a5a8f' },
  { key: 'sent',         label: 'Sent',         color: '#3a5a8f' },
  { key: 'opened',       label: 'Opened',       color: '#a05a00' },
  { key: 'replied',      label: 'Replied',      color: '#2d6e3a' },
  { key: 'booked',       label: 'Booked',       color: '#2d6e3a' },
  { key: 'archived',     label: 'Archived',     color: '#c5c2b8' },
  { key: 'skipped',      label: 'Skipped',      color: '#c5c2b8' },
  { key: 'unsubscribed', label: 'Unsubscribed', color: '#9c2a1f' },
];

function Pipeline({ counts, loading }) {
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="label" style={{ marginBottom: 4 }}>Pipeline</div>
          <h3>Where prospects live right now</h3>
        </div>
        <Link to="/prospects" className="btn btn-ghost btn-sm">All prospects →</Link>
      </div>
      <div className="card-body">
        <div className="stack-3">
          {STAGES.map(s => {
            const n = counts[s.key] || 0;
            const dim = n === 0;
            return (
              <div key={s.key} className="row row-between" style={{ paddingBlock: 4, opacity: dim && !loading ? 0.55 : 1 }}>
                <span className="row row-tight">
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: s.color }} />
                  <span style={{ fontSize: 14 }}>{s.label}</span>
                </span>
                <span className="mono" style={{ fontSize: 14, color: n > 0 ? 'var(--ink)' : 'var(--ink-faint)' }}>
                  {loading ? '—' : n}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function NextSteps({ prospectStats, drafts, loading }) {
  if (loading) {
    return (
      <div className="card card-pad">
        <div className="row muted"><span className="spinner" /> Loading</div>
      </div>
    );
  }

  const totalProspects = Object.values(prospectStats).reduce((a, b) => a + b, 0);
  const items = [];

  if (totalProspects === 0) {
    items.push({
      done: false,
      title: 'Add your first prospect',
      body: 'Manually add a journalist, podcast host, or analyst you want to reach. Discovery routines come online later.',
      cta: { to: '/prospects/new', label: 'Add prospect' },
    });
  } else {
    items.push({ done: true, title: `${totalProspects} prospect${totalProspects === 1 ? '' : 's'} in the system`, body: null });
  }

  if (drafts > 0) {
    items.push({
      done: false,
      title: `Review ${drafts} draft${drafts === 1 ? '' : 's'} in the queue`,
      body: 'Approve, edit, or skip each one. Sends only happen after you click approve.',
      cta: { to: '/queue', label: 'Open queue' },
    });
  } else if (totalProspects > 0) {
    items.push({
      done: false,
      title: 'No drafts yet',
      body: 'Open a prospect and click "Draft a pitch" to have Claude write one for them. Auto-drafting comes online when the worker ships.',
      cta: { to: '/prospects', label: 'Open prospects' },
    });
  }

  items.push({
    done: false,
    title: 'Drop voice samples in /voice-corpus',
    body: 'A few of your real pitches help Claude write in your voice. Cold-start uses Gmail samples, but more is better.',
    cta: null,
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="label" style={{ marginBottom: 4 }}>What to do next</div>
          <h3>Today's checklist</h3>
        </div>
      </div>
      <div className="card-body stack-4">
        {items.map((it, i) => (
          <div key={i} className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
            <span style={{
              width: 16, height: 16, marginTop: 4, borderRadius: 999, flexShrink: 0,
              border: '1.5px solid ' + (it.done ? 'var(--success)' : 'var(--border-strong)'),
              background: it.done ? 'var(--success)' : 'transparent',
              display: 'inline-block',
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 500 }}>{it.title}</div>
              {it.body ? <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 2 }}>{it.body}</div> : null}
              {it.cta ? (
                <Link to={it.cta.to} className="btn btn-ghost btn-sm" style={{ marginTop: 6, padding: '4px 0' }}>{it.cta.label} →</Link>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
